import React from 'react';

import { datadogRum } from '@datadog/browser-rum';
import { useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import { AmplitudeProductEvent } from '@cfa/pathway-fe-shared';
import { patchPlanStatusMutation, Plan } from '@cfa/pathway-fe-shared/xp';

import { itemByLanguage } from '@/utils/language';
import { invalidatePlanArchiveStatusQueries } from '@/utils/queryInvalidation';

import ConfirmationModal from '@/components/popups/ConfirmationModal';
import ToastMessageBlock from '@/components/Toasts/SuccessToast';

import { useAmplitudeTrack } from '@/amplitude/useAmplitude';

interface ArchivePlanProps {
  plan: Plan;
  setShowArchivePlan: (show: boolean) => void;
  showArchivePlan: boolean;
}

const ArchivePlan: React.FC<ArchivePlanProps> = ({
  plan,
  setShowArchivePlan,
  showArchivePlan,
}) => {
  const { t } = useTranslation();
  const track = useAmplitudeTrack();

  const { mutate: updatePlanStatus, isPending } = useMutation({
    ...patchPlanStatusMutation(),
    onSuccess: () => {
      track(AmplitudeProductEvent.ARCHIVE_PLAN_CLICKED, {
        plan_id: plan.id,
        plan_type: plan.type,
      });
      toast.custom(toastObj => (
        <ToastMessageBlock id={toastObj.id}>
          {`${itemByLanguage(plan.name, '')} ${t('TrainingPlans.toastMessage.archived')}`}
        </ToastMessageBlock>
      ));
    },
    onSettled: async () => {
      await invalidatePlanArchiveStatusQueries();
      setShowArchivePlan(false);
    },
    onError: error => {
      datadogRum.addError(
        error instanceof Error ? error : new Error(JSON.stringify(error)),
      );
      toast.custom(toastObj => (
        <ToastMessageBlock id={toastObj.id} severity="error">
          {t('TrainingPlans.toastMessage.errorArchiving')}
        </ToastMessageBlock>
      ));
    },
  });

  const handlePrimaryAction = () => {
    if (isPending) return;
    updatePlanStatus({
      path: { planId: plan.id },
      body: { status: 'INACTIVE' },
    });
  };

  return (
    <ConfirmationModal
      bodyText={t('Generic.archiveObject')}
      headerText={t('Generic.archiveHeader', { type: t('Generic.plan') })}
      isDisabled={isPending}
      isLoading={isPending}
      isOpen={showArchivePlan}
      onClose={() => setShowArchivePlan(false)}
      primaryButtonHandler={handlePrimaryAction}
      primaryButtonText={t('Button.archive')}
      secondaryButtonHandler={() => setShowArchivePlan(false)}
      secondaryButtonText={t('Button.cancel')}
      primaryButtonVariant="destructive"
    />
  );
};
export default ArchivePlan;
